import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Building2,
  TrendingUp,
  Users,
  Star,
  Target,
  Briefcase,
  ArrowRight
} from "lucide-react";
import { cn } from "@/lib/utils";

interface BuyerCardProps {
  buyer: {
    id: string;
    name: string;
    initials?: string;
    subType?: string;
    industry?: string;
    targetIndustries?: string[];
    minEbitda?: string;
    maxEbitda?: string;
    contactCount?: number;
    website?: string;
    notes?: string;
    investmentThesis?: string;
    geoFocus?: string[];
    firmType?: string;
    tier?: number;
  };
  onClick?: () => void;
}

// Canonical buyer firm types per shared/schema.ts + CLAUDE.md.
// Legacy "Financial"/"Strategic"/"Sponsor" values have been migrated out
// of the database and removed from this config.
const buyerTypeConfig: Record<string, { label: string; color: string; icon: any }> = {
  "Private Equity": {
    label: "Private Equity",
    color: "bg-blue-500/10 text-blue-600 border-blue-500/20 dark:text-blue-400",
    icon: TrendingUp
  },
  "Strategic Acquirer": {
    label: "Strategic Acquirer",
    color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400",
    icon: Building2
  },
  "Independent Sponsor": {
    label: "Independent Sponsor",
    color: "bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400",
    icon: Target
  },
  "Family Office": {
    label: "Family Office",
    color: "bg-purple-500/10 text-purple-600 border-purple-500/20 dark:text-purple-400",
    icon: Star
  },
  "Search Fund": {
    label: "Search Fund",
    color: "bg-pink-500/10 text-pink-600 border-pink-500/20 dark:text-pink-400",
    icon: Briefcase
  },
  "Other": {
    label: "Other",
    color: "bg-zinc-500/10 text-zinc-600 border-zinc-500/20 dark:text-zinc-400",
    icon: Briefcase
  }
};

const FALLBACK_BUYER_TYPE = {
  label: "Unclassified",
  color: "bg-muted text-muted-foreground border-border",
  icon: Briefcase,
};

function getTierBadge(tier: number) {
  switch (tier) {
    case 1:
      return { label: "Tier 1", className: "bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border-amber-200 dark:text-amber-400 dark:border-amber-900" };
    case 2:
      return { label: "Tier 2", className: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-900/30 dark:text-slate-400 dark:border-slate-900" };
    default:
      return { label: "Tier 3", className: "bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-900/30 dark:border-gray-900" };
  }
}

export function BuyerCard({ buyer, onClick }: BuyerCardProps) {
  const tier = getTierBadge(buyer.tier || 3);
  
  const buyerType = buyerTypeConfig[buyer.subType || ""] || FALLBACK_BUYER_TYPE;
  const TypeIcon = buyerType.icon;

  return (
    <Card 
      className={cn(
        "group relative overflow-hidden",
        "border border-border/50 hover:border-primary/30",
        "bg-gradient-to-br from-card to-card/95",
        "hover:shadow-xl hover:shadow-primary/5",
        "transition-all duration-300 cursor-pointer",
        "hover:-translate-y-1"
      )}
      onClick={onClick}
    >
      {/* Top accent bar */}
      <div className="h-1 w-full bg-primary/30" />
      
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div className={cn(
              "w-12 h-12 rounded-xl flex items-center justify-center",
              "bg-gradient-to-br from-primary/10 to-primary/5",
              "border border-primary/20",
              "group-hover:from-primary/20 group-hover:to-primary/10",
              "transition-all duration-300"
            )}>
              <span className="text-lg font-bold text-primary-text">
                {buyer.initials || buyer.name.slice(0, 2).toUpperCase()}
              </span>
            </div>
            
            <div className="min-w-0">
              <h3 className="font-semibold text-foreground group-hover:text-primary-text transition-colors truncate">
                {buyer.name}
              </h3>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant="outline" className={cn("text-xs", buyerType.color)}>
                  <TypeIcon className="w-3 h-3 mr-1" />
                  {buyerType.label}
                </Badge>
                {buyer.tier && buyer.tier <= 2 && (
                  <Badge variant="outline" className={cn("text-xs", tier.className)}>
                    <Star className="w-3 h-3 mr-1" />
                    {tier.label}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
        </div>

        {/* Investment Focus */}
        <div className="space-y-2 mb-4">
          {buyer.targetIndustries && buyer.targetIndustries.length > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <Target className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">Focus:</span>
              <div className="flex flex-wrap gap-1">
                {buyer.targetIndustries.slice(0, 3).map((industry, i) => (
                  <span key={i} className="text-foreground text-xs bg-muted px-2 py-0.5 rounded">
                    {industry}
                  </span>
                ))}
                {buyer.targetIndustries.length > 3 && (
                  <span className="text-muted-foreground text-xs">
                    +{buyer.targetIndustries.length - 3}
                  </span>
                )}
              </div>
            </div>
          )}
          
          {(buyer.minEbitda || buyer.maxEbitda) && (
            <div className="flex items-center gap-2 text-sm">
              <TrendingUp className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">EBITDA:</span>
              <span className="text-foreground">
                {buyer.minEbitda || "—"} - {buyer.maxEbitda || "—"}
              </span>
            </div>
          )}
          
        </div>

        {/* Investment Thesis Preview */}
        {buyer.investmentThesis && (
          <div className="mb-4 p-3 bg-muted/50 rounded-lg border border-border/50">
            <p className="text-xs text-muted-foreground line-clamp-2">
              "{buyer.investmentThesis}"
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-border/50">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Users className="w-4 h-4" />
              <span>{buyer.contactCount || 0} contacts</span>
            </div>
          </div>
          
          <Button 
            variant="ghost" 
            size="sm" 
            className="gap-1 text-primary-text hover:text-primary-text hover:bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            View Profile
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
      
      {/* Hover glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
    </Card>
  );
}

export function BuyerCardSkeleton() {
  return (
    <Card className="border border-border/50 overflow-hidden">
      <div className="h-1 w-full bg-muted" />
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-muted animate-pulse" />
            <div className="space-y-2">
              <div className="w-32 h-5 bg-muted rounded animate-pulse" />
              <div className="w-20 h-4 bg-muted rounded animate-pulse" />
            </div>
          </div>
          <div className="w-16 h-8 bg-muted rounded animate-pulse" />
        </div>
        <div className="space-y-2 mb-4">
          <div className="w-full h-4 bg-muted rounded animate-pulse" />
          <div className="w-3/4 h-4 bg-muted rounded animate-pulse" />
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-border/50">
          <div className="w-24 h-4 bg-muted rounded animate-pulse" />
          <div className="w-20 h-8 bg-muted rounded animate-pulse" />
        </div>
      </CardContent>
    </Card>
  );
}
